import { Component, inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  ButtonComponent,
  DropdownFieldWithErrorComponent,
  DropdownOption,
  FormSectionComponent,
  TextFieldWithErrorComponent,
  urlValidator
} from 'htx-ui-library';
import { FormErrorMessagesService } from '../../../../../services/form/form-error-messages.service';
import { DiscoveryOrganization } from '../../../../../services/infrastructure/infrastructure.model';

const BPNL_PREFIX = 'BPNL';
const URL_PREFIX = 'https://';

export interface DiscoveryFormDialogData {
  mode: 'add' | 'edit';
  organization?: DiscoveryOrganization;
}

export interface DiscoveryFormResult {
  bpnl: string;
  organizationName: string;
  connectorUrl: string | null;
  useCases: string[];
}

@Component({
  selector: 'app-discovery-form-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    TranslocoDirective,
    ButtonComponent,
    FormSectionComponent,
    TextFieldWithErrorComponent,
    DropdownFieldWithErrorComponent
  ],
  templateUrl: './discovery-form-dialog.component.html',
  styleUrl: './discovery-form-dialog.component.scss'
})
export class DiscoveryFormDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private transloco = inject(TranslocoService);
  private dialogRef = inject(MatDialogRef<DiscoveryFormDialogComponent, DiscoveryFormResult>);
  readonly data = inject<DiscoveryFormDialogData>(MAT_DIALOG_DATA);
  readonly errorMessagesService = inject(FormErrorMessagesService);

  readonly isEdit = this.data.mode === 'edit';

  useCaseOptions: DropdownOption[] = [];

  form = this.fb.group({
    bpnl: [
      { value: BPNL_PREFIX, disabled: this.isEdit },
      [Validators.required, Validators.maxLength(50)]
    ],
    organizationName: ['', [Validators.required, Validators.maxLength(255)]],
    connectorUrl: [URL_PREFIX, [urlValidator, Validators.maxLength(150)]],
    useCases: this.fb.nonNullable.control<string[]>([])
  });

  ngOnInit(): void {
    const prefix = 'dsaManagement.infrastructure.discoveryForm.useCaseOptions';
    this.useCaseOptions = [
      {
        value: 'CO2_FOOTPRINT_MANAGEMENT',
        label: this.transloco.translate(`${prefix}.co2FootprintManagement`)
      },
      {
        value: 'SUPPLY_CHAIN_DIGITIZATION',
        label: this.transloco.translate(`${prefix}.supplyChainDigitization`)
      },
      {
        value: 'SUPPLY_SHORTAGE',
        label: this.transloco.translate(`${prefix}.supplyShortage`)
      }
    ];

    const org = this.data.organization;
    if (org) {
      this.form.patchValue({
        bpnl: org.bpnl,
        organizationName: org.organizationName,
        connectorUrl: org.connectorUrl ?? URL_PREFIX,
        useCases: org.useCases
      });
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const connectorUrl = (value.connectorUrl ?? '').trim();
    // A bare "https://" prefix counts as "no connector URL".
    const cleanedUrl = connectorUrl.length > 0 && connectorUrl !== URL_PREFIX ? connectorUrl : null;

    this.dialogRef.close({
      bpnl: (value.bpnl ?? '').trim(),
      organizationName: (value.organizationName ?? '').trim(),
      connectorUrl: cleanedUrl,
      useCases: value.useCases ?? []
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
